import { authService } from '../services/authService'
import { getApiBaseUrl, setApiBaseUrl } from './config'

/** Error carrying the HTTP status + parsed response body (e.g. `{ errors: [...] }`). */
export class ApiError extends Error {
  constructor(
    public status: number,
    public body: unknown,
  ) {
    super(`HTTP error! status: ${status}`)
    this.name = 'ApiError'
  }

  /** Structured `errors` list returned by validation/pipeline failures. */
  get messages(): string[] {
    if (this.body && typeof this.body === 'object' && 'errors' in this.body) {
      const errs = (this.body as { errors: unknown }).errors
      if (Array.isArray(errs)) {
        return errs.map(String)
      }
    }
    return [this.message]
  }
}

class ApiClient {
  setBaseUrl(url: string) {
    setApiBaseUrl(url)
  }

  private resolveUrl(endpoint: string): string {
    if (endpoint.startsWith('http')) {
      return endpoint
    }
    const base = getApiBaseUrl().replace(/\/$/, '')
    return `${base}${endpoint}`
  }

  async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    const token = authService.getToken()

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...(options.headers as Record<string, string>)
    }

    if (token) {
      headers['Authorization'] = `Bearer ${token}`
    }

    const scopeJson = localStorage.getItem('nyos_active_scope')
    if (scopeJson) {
      try {
        const scope = JSON.parse(scopeJson)
        if (scope?.facilityId) {
          headers['X-Facility-ID'] = scope.facilityId
        }
      } catch {
        // ignore malformed stored scope
      }
    }

    const response = await fetch(this.resolveUrl(endpoint), {
      ...options,
      headers
    })

    if (response.status === 401) {
      // Token expired or invalid
      authService.logout()
      window.location.href = '/login'
      throw new Error('Unauthorized')
    }

    if (!response.ok) {
      let body: unknown = null
      try {
        body = await response.json()
      } catch {
        // non-JSON error body — status is all we have
      }
      throw new ApiError(response.status, body)
    }

    return response.json()
  }

  get<T>(endpoint: string): Promise<T> {
    return this.request<T>(endpoint, { method: 'GET' })
  }

  post<T>(endpoint: string, data: any): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'POST',
      body: JSON.stringify(data)
    })
  }

  put<T>(endpoint: string, data: any): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'PUT',
      body: JSON.stringify(data)
    })
  }

  delete<T>(endpoint: string): Promise<T> {
    return this.request<T>(endpoint, { method: 'DELETE' })
  }

  patch<T>(endpoint: string, data: any): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'PATCH',
      body: JSON.stringify(data)
    })
  }
}

export const apiClient = new ApiClient()
